export type SearchType =
  | "po-numbers"
  | "request-id"
  | "cutting-date"
  | "cutting-id"
  | "invalid";

export interface SearchTypeClassification {
  type: SearchType;
  normalizedInput: string;
  tokens: string[];
  reason?: string;
}

const REQUEST_ID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CUTTING_DATE_REGEX =
  /^(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(19|20)\d{2}$/i;
const CUTTING_ID_REGEX = /^CID-\d{6,10}$/i;
const PO_TOKEN_REGEX = /^(\d{7}[A-Z]{3}|[A-Z0-9]+\/[A-Z0-9]+)$/;

function normalizePurchaseOrderToken(token: string): string {
  return token.replaceAll("/", "");
}

export function normalizeSearchText(value: string): string {
  return value
    .replaceAll(/[,;:\r\n\t]+/g, " ")
    .replaceAll(/ {2,}/g, " ")
    .trim();
}

export function classifySearchInput(
  rawInput: string
): SearchTypeClassification {
  const normalizedInput = normalizeSearchText(rawInput).toUpperCase();
  if (!normalizedInput) {
    return {
      type: "invalid",
      normalizedInput,
      tokens: [],
      reason: "Enter a search value.",
    };
  }

  const tokens = normalizedInput.split(" ").filter(Boolean);

  if (tokens.length === 1) {
    const singleToken = tokens[0];
    if (!singleToken) {
      return {
        type: "invalid",
        normalizedInput,
        tokens,
        reason: "Enter a search value.",
      };
    }

    if (singleToken.includes("/") && singleToken.split("/").length === 2) {
      return {
        type: "po-numbers",
        normalizedInput: normalizePurchaseOrderToken(singleToken),
        tokens: [normalizePurchaseOrderToken(singleToken)],
      };
    }

    if (/^\d{7}[A-Z]{3}$/.test(singleToken)) {
      return { type: "po-numbers", normalizedInput, tokens };
    }

    if (REQUEST_ID_REGEX.test(singleToken)) {
      return { type: "request-id", normalizedInput, tokens };
    }

    if (CUTTING_DATE_REGEX.test(singleToken)) {
      return { type: "cutting-date", normalizedInput, tokens };
    }

    if (CUTTING_ID_REGEX.test(singleToken)) {
      return { type: "cutting-id", normalizedInput, tokens };
    }
  }

  const allPoTokens = tokens.every((token) => PO_TOKEN_REGEX.test(token));
  if (allPoTokens) {
    const normalizedPoTokens = tokens.map((token) =>
      normalizePurchaseOrderToken(token)
    );
    return {
      type: "po-numbers",
      normalizedInput: normalizedPoTokens.join(" "),
      tokens: normalizedPoTokens,
    };
  }

  return {
    type: "invalid",
    normalizedInput,
    tokens,
    reason:
      "Input is not recognized. Use a Request ID, Cutting Date (MMDDYYYY), Cutting ID (CID-######), or PO number(s).",
  };
}

export function getSearchTypeLabel(type: SearchType): string {
  switch (type) {
    case "request-id": {
      return "Request ID Search";
    }
    case "cutting-date": {
      return "Cutting Date Search";
    }
    case "cutting-id": {
      return "Cutting ID Search";
    }
    case "po-numbers": {
      return "PO Number Search";
    }
    default: {
      return "Search";
    }
  }
}

export function getLoadingMessage(
  type: SearchType,
  tokenCount: number
): string {
  switch (type) {
    case "request-id": {
      return "Searching by Purchase Order Request ID...";
    }
    case "cutting-date": {
      return "Searching by Cutting Date...";
    }
    case "cutting-id": {
      return "Searching by Cutting ID...";
    }
    case "po-numbers": {
      return tokenCount > 1
        ? "Searching by PO Numbers..."
        : "Searching by PO Number...";
    }
    default: {
      return "Searching...";
    }
  }
}

export function getNoResultsMessage(type: SearchType): string {
  switch (type) {
    case "request-id": {
      return "No results found for this Request ID. Please check your input and try again.";
    }
    case "cutting-date": {
      return "No results found for this Cutting Date. Please check your input and try again.";
    }
    case "cutting-id": {
      return "No results found for this Cutting ID. Please check your input and try again.";
    }
    case "po-numbers": {
      return "No results found for these PO Number(s). Please check your input and try again.";
    }
    default: {
      return "No results found. Please check your input and try again.";
    }
  }
}
